
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

function RevealDiv({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const stats = [
    { number: '5+', label: 'Years in Business' },
    { number: '50+', label: 'Happy Clients' },
    { number: '30+', label: 'Projects Completed' },
    { number: '98%', label: 'Client Satisfaction' }
  ];

  const teamMembers = [
    {
      name: 'Ruswata Allan',
      role: 'Project Manager',
      image: 'https://res.cloudinary.com/dodsf90se/image/upload/v1774518967/rusatwa_fjgmg9.jpg',
    
    },
        {
      name: 'Aliane Umererwa',
      role: 'Customer Success Manager',
      image: 'https://res.cloudinary.com/dodsf90se/image/upload/v1774518338/aliane_cb9ygj.jpg',

    },
    {
      name: 'Bienvenue Masengesho',
      role: 'Lead Developer',
      image: 'https://res.cloudinary.com/dodsf90se/image/upload/v1774533066/fnal-web_gb6sng.jpg',
      bio: ''
    },
    {
      name: 'Esther Amumpaire',
      role: 'Digital Marketing Manager',
      image: 'https://res.cloudinary.com/dodsf90se/image/upload/q_auto/f_auto/v1775984620/clothes_change_ai_1775983627802_qcol9q.jpg',
      bio: ''
    },
     {
      name: 'Allan Muhwezi',
      role: 'Business Development',
      image: 'https://res.cloudinary.com/dodsf90se/image/upload/q_auto/f_auto/v1775985453/clothes_change_ai_1775985376117_fzy6uu.jpg',
      bio: ''
    },
    {
      name: 'Jeannet M',
      role: 'Social Media Manager',
      image: 'https://res.cloudinary.com/dodsf90se/image/upload/v1774517908/jeannet_leupwb.jpg',
      bio: ''
    },
      {
      name: 'Mugisha Theophile',
      role: 'UI/UX Designer',
      image: 'https://res.cloudinary.com/dodsf90se/image/upload/v1774705852/mugish_svbncd.jpg',
      bio: ''
    },
    {
      name: 'Solange',
      role: 'Content Strategist',
      image: 'https://res.cloudinary.com/dodsf90se/image/upload/v1774518350/soso2_zrsb01.jpg',
      bio: ''
    },
  
  ];

  const values = [
    {
      icon: 'ri-star-smile-line',
      title: 'Client-Centric',
      description: 'We prioritize your needs and work tirelessly to exceed expectations'
    },
    {
      icon: 'ri-innovation-line',
      title: 'Innovation',
      description: 'We stay ahead of the curve with cutting-edge technologies and strategies'
    },
    {
      icon: 'ri-team-line',
      title: 'Teamwork',
      description: 'Collaboration is at the heart of everything we do'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Reliability',
      description: 'We deliver on our promises, on time and on budget'
    }
  ];

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-800 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.2s' }}></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="font-display text-5xl md:text-6xl font-bold mb-6" style={{ animation: 'fadeSlideDown 0.8s ease both' }}>
              {t('profile.title', 'About BientoMart')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 leading-relaxed" style={{ animation: 'fadeSlideDown 0.8s ease 0.2s both' }}>
              {t('profile.subtitle', 'Your trusted digital partner in Uganda')}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <RevealDiv key={index} delay={index * 100} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-teal-500 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </RevealDiv>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <RevealDiv>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-teal-500/20 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl"></div>
                <img
                  src="https://res.cloudinary.com/dodsf90se/image/upload/v1774525934/top4_pvwq5o.jpg"
                  alt="BientoMart Team"
                  className="relative rounded-2xl shadow-2xl w-full h-auto"
                />
              </div>
            </RevealDiv>
            <RevealDiv delay={200}>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">{t('profile.aboutTitle', 'Empowering Businesses Through Digital Innovation')}</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('profile.aboutP1', "BientoMart is Uganda's leading digital services platform, helping businesses and entrepreneurs build powerful online presences since 2020.")}
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('profile.aboutP2', 'Our team of expert designers, marketers, and AI specialists works tirelessly to deliver exceptional results for clients across East Africa.')}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t('profile.aboutP3', 'We combine cutting-edge technology with deep local market knowledge to create digital strategies that truly resonate with Ugandan audiences.')}
              </p>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-teal-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                {t('profile.cta', 'Get in Touch')}
              </button>
            </RevealDiv>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <RevealDiv>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">{t('profile.valuesTitle', 'Our Core Values')}</h2>
              <p className="text-xl text-gray-600">{t('profile.valuesSubtitle', 'The principles that guide everything we do')}</p>
            </div>
          </RevealDiv>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <RevealDiv key={index} delay={index * 100}>
                <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-600 to-teal-500 rounded-full mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${value.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              </RevealDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <RevealDiv>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">{t('profile.teamTitle', 'Meet Our Team')}</h2>
              <p className="text-xl text-gray-600">{t('profile.teamSubtitle', 'The passionate people behind BientoMart')}</p>
            </div>
          </RevealDiv>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => {
              const isFounder = member.name === 'Bienvenue Masengesho';
              return (
                <RevealDiv key={index} delay={index * 100}>
                  {isFounder ? (
                    <Link
                      to="/bienvenu-masengesho"
                      className="block bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group"
                    >
                      <div className="relative w-full h-64 overflow-hidden">
                        <img
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                        <p className="text-blue-600 font-medium mb-3">{member.role}</p>
                        <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                        <span className="inline-flex items-center gap-1 text-clairBlue-500 text-sm font-semibold mt-4 group-hover:gap-2 transition-all">
                          View Full Profile <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </Link>
                  ) : (
                    <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group">
                      <div className="relative w-full h-64 overflow-hidden">
                        <img
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                        <p className="text-blue-600 font-medium mb-3">{member.role}</p>
                        <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                      </div>
                    </div>
                  )}
                </RevealDiv>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <RevealDiv>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">{t('profile.whyTitle', 'Why Choose BientoMart')}</h2>
              <p className="text-xl text-gray-600">{t('profile.whySubtitle', 'What sets us apart from the rest')}</p>
            </div>
          </RevealDiv>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-time-line',
                title: t('profile.why1Title', 'Fast Delivery'),
                desc: t('profile.why1Desc', 'We deliver projects on time, every time. No delays, no excuses.')
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: t('profile.why2Title', 'Affordable Pricing'),
                desc: t('profile.why2Desc', 'Competitive rates tailored to Ugandan businesses and budgets.')
              },
              {
                icon: 'ri-customer-service-2-line',
                title: t('profile.why3Title', '24/7 Support'),
                desc: t('profile.why3Desc', 'Round-the-clock support to ensure your business never stops.')
              }
            ].map((item, index) => (
              <RevealDiv key={index} delay={index * 100}>
                <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-600 to-teal-500 rounded-full mx-auto mb-6">
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </RevealDiv>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-blue-900 to-teal-800">
        <div className="container mx-auto px-6 text-center">
          <RevealDiv>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{t('profile.ctaTitle', 'Ready to Transform Your Business?')}</h2>
            <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto">{t('profile.ctaSubtitle', "Let's create something amazing together. Contact us today to discuss your project")}</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-white text-blue-900 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
              >
                {t('profile.ctaButton', 'Get Started')}
              </button>
              <a
                href="tel:+256773515447"
                className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
              >
                {t('profile.callUs', 'Call Us')}
              </a>
            </div>
          </RevealDiv>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ProfilePage;
